import React, { useEffect, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { TbChevronDown } from 'react-icons/tb';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useLayoutContext } from '../../../contexts/useLayoutContext';
import { scrollToElement } from '../../../helpers/layout';
import routes from '../../../routes';
import { AdminRole, AdminRoleName } from '../../../constants/AdminRole';

function MenuItemWithChildren({ item, openMenuKey, setOpenMenuKey, level = 0 }) {
  const { pathname } = useLocation();
  const isTopLevel = level === 0;

  const [localOpen, setLocalOpen] = useState(false);
  const [didAutoOpen, setDidAutoOpen] = useState(false);

  const renderBadgeOrArrow = (item) => {
    if (item.badge) {
      if (React.isValidElement(item.badge)) {
        return item.badge;
      }
      return <span className={`badge bg-${item.badge.variant}`}>{item.badge.text}</span>;
    }
    return (
      <span className="menu-arrow">
        <TbChevronDown />
      </span>
    );
  };

  const isChildActive = (children) =>
    children.some(
      (child) =>
        (child.url && pathname.endsWith(child.url)) ||
        (child.children && isChildActive(child.children)),
    );

  const isActive = isChildActive(item.children || []);

  const isOpen = isTopLevel ? openMenuKey === item.key : localOpen;

  useEffect(() => {
    if (isTopLevel && isActive && !didAutoOpen) {
      setOpenMenuKey(item.key);
      setDidAutoOpen(true);
    }
    if (!isTopLevel && isActive && !didAutoOpen) {
      setLocalOpen(true);
      setDidAutoOpen(true);
    }
  }, [isActive, isTopLevel, item.key, setOpenMenuKey, didAutoOpen]);

  const toggleOpen = () => {
    if (isTopLevel) {
      setOpenMenuKey(isOpen ? null : item.key);
    } else {
      setLocalOpen((prev) => !prev);
    }
  };

  return (
    <li className={`side-nav-item ${isOpen ? 'active' : ''}`}>
      <button
        onClick={toggleOpen}
        className="side-nav-link d-flex justify-content-between align-items-center"
        aria-expanded={isOpen}
        type="button"
      >
        {item.icon && (
          <span className="menu-icon icon-wrapper">
            <img src={item.icon} alt={item.text} className="icon-image" />
          </span>
        )}
        <span className="menu-text">{item.label}</span>
        {renderBadgeOrArrow(item)}
      </button>
      <Collapse in={isOpen}>
        <div>
          <ul className="sub-menu">
            {(item.children || [])
              .filter((child) => child.label && (child.url || child.children))
              .map((child) =>
                child.children ? (
                  <MenuItemWithChildren
                    key={child.key}
                    item={child}
                    openMenuKey={openMenuKey}
                    setOpenMenuKey={setOpenMenuKey}
                    level={level + 1}
                  />
                ) : (
                  <MenuItem key={child.key} item={child} />
                ),
              )}
          </ul>
        </div>
      </Collapse>
    </li>
  );
}

function MenuItem({ item }) {
  const { pathname } = useLocation();
  const isActive = item.url && pathname.endsWith(item.url);

  const { sidenav, hideBackdrop } = useLayoutContext();

  const renderBadge = (badge) => {
    if (React.isValidElement(badge)) {
      return badge;
    }
    return <span className={`badge text-bg-${badge.variant} opacity-50`}>{badge.text}</span>;
  };

  const toggleBackdrop = () => {
    if (sidenav.size === 'offcanvas') {
      hideBackdrop();
    }
  };

  return (
    <li className={`side-nav-item ${isActive ? 'active' : ''}`}>
      <Link
        to={item.url ?? '/'}
        onClick={toggleBackdrop}
        className={`side-nav-link  ${isActive ? 'active' : ''} ${item.isDisabled ? 'disabled' : ''} ${item.isSpecial ? 'special-menu' : ''}`}
      >
        {item.icon && (
          <span className="menu-icon icon-wrapper">
            <img src={item.icon} alt={item.text} className="icon-image" />
          </span>
        )}
        <span className="menu-text">{item.label}</span>
        {item.badge && renderBadge(item.badge)}
      </Link>
    </li>
  );
}

function AppMenu() {
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const accountType = useSelector((state) => state.auth.get('accountType'));

  const scrollToActiveLink = () => {
    const activeItem = document.querySelector('.side-nav-link.active');
    if (activeItem) {
      const simpleBarContent = document.querySelector('#sidenav .simplebar-content-wrapper');
      if (simpleBarContent) {
        const offset = activeItem.offsetTop - window.innerHeight * 0.4;
        scrollToElement(simpleBarContent, offset, 500);
      }
    }
  };

  useEffect(() => {
    setTimeout(() => scrollToActiveLink(), 100);
  }, []);

  const filteredRoutes = routes.filter((item) => {
    if (!item.isTitle && (!item.label || (!item.url && !item.children))) {
      return false;
    }

    if (item.isTitle) {
      return true;
    }

    if (accountType === AdminRole.DASHBOARD) {
      return item.access === AdminRoleName.DASHBOARD;
    }

    if (accountType === AdminRole.SUPER_ADMIN) {
      return item.access !== AdminRoleName.DASHBOARD;
    }

    if (accountType === AdminRole.ADMIN) {
      return item.access === AdminRoleName.ADMIN;
    }

    return true;
  });

  return (
    <ul className="side-nav">
      {filteredRoutes.map((item) =>
        // eslint-disable-next-line no-nested-ternary
        item.isTitle ? (
          <li className="side-nav-title" key={item.key}>
            {item.label}
          </li>
        ) : item.children ? (
          <MenuItemWithChildren
            key={item.key}
            item={item}
            openMenuKey={openMenuKey}
            setOpenMenuKey={setOpenMenuKey}
          />
        ) : (
          <MenuItem key={item.key} item={item} />
        ),
      )}
    </ul>
  );
}

export default AppMenu;
